/**
 * Null-object defaults for every published interface. Owned by core.
 *
 * WHY THIS EXISTS: eight subsystems are built in parallel. Without these, every subsystem
 * would need `if (ctx.world)` guards everywhere, and those guards would survive into the
 * finished game as dead branches. Instead core publishes a working default for each
 * interface at boot; the owning subsystem replaces it in `init()`.
 *
 * The fallback world is deliberately useful, not empty: a flat plane with one straight
 * two-lane street running along +Z. That is enough for `vehicle` to tune physics, `traffic`
 * to drive cars, and `gameplay` to place potholes, before `world` has landed a city.
 *
 * If you find yourself relying on fallback behaviour in shipped code, that is a bug.
 * Check `ctx.world.isFallback` if you genuinely need to know.
 */

import * as THREE from 'three';

const FLAT_ROAD_WIDTH = 11;
const FLAT_LANES = 2;

/** WorldQuery over an infinite flat plane with one straight street along the Z axis. */
export function makeFallbackWorld() {
  const dir = { x: 0, y: 1 };            // 2D direction (x, z)
  const seg = { id: 0, a: new THREE.Vector3(0, 0, -400), b: new THREE.Vector3(0, 0, 400), width: FLAT_ROAD_WIDTH, lanes: FLAT_LANES, dir, kind: 'street' };

  return {
    isFallback: true,
    bounds: { minX: -200, maxX: 200, minZ: -400, maxZ: 400 },

    sampleGround(x /* , z */) {
      const onRoad = Math.abs(x) <= FLAT_ROAD_WIDTH * 0.5;
      const onWalk = !onRoad && Math.abs(x) <= FLAT_ROAD_WIDTH * 0.5 + 3.2;
      return {
        y: onWalk ? 0.14 : 0,
        normal: UP,
        surface: onRoad ? 'road' : onWalk ? 'sidewalk' : 'grass',
      };
    },

    roadAt(x /* , z */) {
      const half = FLAT_ROAD_WIDTH * 0.5;
      const onRoad = Math.abs(x) <= half;
      const lane = x < 0 ? 0 : 1;
      return { onRoad, lane, dir, width: FLAT_ROAD_WIDTH, edgeDist: half - Math.abs(x) };
    },

    nearestRoadPoint(x, z) { return { x: 0, z, dir, segmentId: 0 }; },
    segment() { return seg; },
    segments() { return [seg]; },

    *spawnPoints(kind) {
      const step = kind === 'pothole' ? 34 : kind === 'traffic' ? 60 : 22;
      for (let z = -300; z <= 300; z += step) {
        const side = ((z / step) | 0) % 2 === 0 ? -1 : 1;
        yield { pos: new THREE.Vector3(side * FLAT_ROAD_WIDTH * 0.24, 0, z), dir };
      }
    },

    collide() { return EMPTY; },
    raycastStatic() { return null; },
    landmarkNear() { return { name: 'rue Test', kind: 'street', dist: 0 }; },
  };
}

const UP = Object.freeze(new THREE.Vector3(0, 1, 0));
const EMPTY = Object.freeze([]);

/** PlayerState default — a truck parked at the origin. */
export function makeFallbackPlayer() {
  return {
    isFallback: true,
    position: new THREE.Vector3(0, 0, 0),
    quaternion: new THREE.Quaternion(),
    velocity: new THREE.Vector3(),
    speed: 0, speedKmh: 0,
    forward: new THREE.Vector3(0, 0, 1),
    gear: 'D',
    slipAngle: 0,
    isDrifting: false,
    isAirborne: false,
    wheelContacts: [0, 1, 2, 3].map(() => ({ grounded: true, surface: 'road', slip: 0, compression: 0.5 })),
    chassisTilt: { pitch: 0, roll: 0 },
    damage: 0,
  };
}

/** GameplayQuery default. */
export function makeFallbackGameplay() {
  return {
    isFallback: true,
    timeRemaining: 60, score: 0, cash: 0,
    combo: 0, multiplier: 1,
    repaired: 0, total: 0,
    activeTarget: null,
    repairSession: null,
    phase: 'playing',
    weather: 'thaw',
    potholes() { return EMPTY; },
    cones() { return EMPTY; },
  };
}

/**
 * FxRequests default — silently swallows everything. Any subsystem may call these from the
 * first line of code it writes, whether or not `fx` has landed.
 */
export function makeFallbackFx() {
  const noop = () => {};
  return {
    isFallback: true,
    splatter: noop, spray: noop, dust: noop, slushKick: noop, waterSpray: noop,
    sparks: noop, debris: noop, tyreMark: noop, decal: noop,
    shake: noop, hitFlash: noop, freezeFrame: noop, popText: noop,
  };
}

/** AudioRequests default. */
export function makeFallbackAudio() {
  const noop = () => {};
  return {
    isFallback: true,
    sfx: noop, announcer: noop, setEngine: noop, setAmbience: noop, music: noop, duck: noop,
  };
}

/** UI default — used by anything that wants to push a banner before `ui` exists. */
export function makeFallbackUi() {
  return { isFallback: true, announce() {}, setHudVisible() {} };
}

/**
 * TrafficQuery default. `gameplay` needs traffic positions to decide whether a car has
 * driven over an uncured patch (`patch:ruined { by:'traffic' }`). A positional event per
 * car per frame would flood the bus, so traffic publishes a queryable handle instead.
 * `traffic` replaces this in init(). See ARCHITECTURE.md §4.12.
 */
export function makeFallbackTraffic() {
  return {
    isFallback: true,
    count: 0,
    vehicles() { return EMPTY; },
    cyclists() { return EMPTY; },
    pedestrians() { return EMPTY; },
    nearest() { return null; },
  };
}

export function installFallbacks(ctx) {
  if (!ctx.world) ctx.world = makeFallbackWorld();
  if (!ctx.player) ctx.player = makeFallbackPlayer();
  if (!ctx.gameplay) ctx.gameplay = makeFallbackGameplay();
  if (!ctx.traffic) ctx.traffic = makeFallbackTraffic();
  if (!ctx.fx) ctx.fx = makeFallbackFx();
  if (!ctx.audio) ctx.audio = makeFallbackAudio();
  if (!ctx.ui) ctx.ui = makeFallbackUi();
  return ctx;
}

/**
 * DEVELOPMENT SCAFFOLD — lights and a ground plane, installed by CORE, only while the
 * owning subsystem is still a stub.
 *
 * Why this exists: `look` owns all lighting (N10), so until it lands there are zero lights
 * in the scene and every MeshStandardMaterial renders pure black. That would block all
 * seven other agents from verifying their own work — a geometry owner cannot tell a correct
 * mesh from a broken one in an all-black frame. Rather than have each agent smuggle in its
 * own lights (which would violate N10 seven times over and then have to be unpicked), core
 * provides one scaffold and removes it the moment the real owner arrives.
 *
 * A hemisphere + directional rig is deliberate: MeshBasicMaterial would be simpler but
 * unlit, and then geometry owners could not judge silhouette, form or normals — which is
 * most of what they need to look at.
 *
 * Everything here is tagged `userData.devScaffold` and is NEVER built once the real owner
 * has landed. If you see this in a finished build, that is a bug.
 */
export function installDevScaffold(ctx) {
  const built = [];

  // Gate the LIGHT rig on `look`, not on materials. (Round-1 bug: it was gated on
  // ctx.materials.isFallback, so when the material library landed before the look system
  // the placeholder lights silently stopped being installed — leaving an unlit scene and
  // pushing another agent into adding its own lights in violation of N10.)
  const lookSys = ctx.sys ? ctx.sys('look') : null;
  const lookIsStub = !lookSys || lookSys.isNull || !!lookSys.stub;

  // If anything has already put a light in the scene, do not double up.
  let sceneHasLight = false;
  ctx.scene.traverse((o) => { if (o.isLight) sceneHasLight = true; });

  if (lookIsStub && !sceneHasLight) {
    const hemi = new THREE.HemisphereLight(0x9fc4f0, 0x50463c, 1.4);
    hemi.name = 'devScaffold:hemi';
    const sun = new THREE.DirectionalLight(0xfff4e2, 2.6);
    sun.position.set(-38, 46, 26);
    sun.name = 'devScaffold:sun';
    built.push(hemi, sun);
  }
  if (lookIsStub && !ctx.scene.background) {
    // A void-grey background so unlit geometry still reads against something.
    ctx.scene.background = new THREE.Color(0x5a6675);
  }

  const worldIsStub = !!(ctx.world && ctx.world.isFallback);

  if (worldIsStub) {
    // Flat ground matching makeFallbackWorld: road strip along +Z, sidewalks either side.
    const road = new THREE.Mesh(
      new THREE.PlaneGeometry(FLAT_ROAD_WIDTH, 800),
      ctx.materials.get('road.asphalt')
    );
    road.rotation.x = -Math.PI / 2;
    road.name = 'devScaffold:road';

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 800),
      ctx.materials.get('road.sidewalk')
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    ground.name = 'devScaffold:ground';
    built.push(ground, road);
  }

  for (const o of built) { o.userData.devScaffold = true; ctx.scene.add(o); }

  ctx._devScaffold = built;
  if (built.length) {
    console.info(
      `[core] dev scaffold active (${built.map((o) => o.name).join(', ')}). ` +
      'This is placeholder geometry/lighting and disappears once look/world land.'
    );
  }
  return built;
}

export function disposeDevScaffold(ctx) {
  for (const o of ctx._devScaffold || []) {
    ctx.scene.remove(o);
    if (o.geometry) o.geometry.dispose();
  }
  ctx._devScaffold = [];
}
